import { categories } from './categoryTree';
import { businessTypes, UNASSIGNED_CATEGORY_KEYS } from './businessTypes';
import { canonicalizeCategoryKey } from './legacyAliases';

export function getTranslatedLabel(data, key, language = 'fr') {
  if (typeof key !== 'string' && key != null) {
    key = String(key);
  }
  if (!data || !key || !data[key]) {
    return typeof key === 'string' ? key : '';
  }
  const item = data[key];
  if (typeof item === 'object' && item !== null) {
    const label = language === 'ar' ? item.nameAr : item.name;
    return typeof label === 'string' ? label : typeof key === 'string' ? key : '';
  }
  return typeof key === 'string' ? key : '';
}

export function resolveBusinessTypeId(businessType) {
  if (!businessType) return null;
  if (typeof businessType === 'string') return businessType;
  return businessType.id || businessType.key || null;
}

export function listBusinessTypes() {
  return Object.entries(businessTypes).map(([id, cfg]) => ({
    id,
    name: cfg.name,
    nameAr: cfg.nameAr,
    description: cfg.description,
    descriptionAr: cfg.descriptionAr,
    allowedCategories: [...cfg.allowedCategories],
  }));
}

export function isCategoryAllowedForBusiness(businessType, categoryKey) {
  const typeId = resolveBusinessTypeId(businessType);
  if (!typeId || !businessTypes[typeId]) return true;
  const canonical = canonicalizeCategoryKey(categoryKey, categories);
  return businessTypes[typeId].allowedCategories.includes(canonical);
}

export const getSubcategoriesForCategory = (categoryKey) => {
  const key = canonicalizeCategoryKey(categoryKey, categories);
  return categories[key]?.subcategories || {};
};

export const getTypesForSubcategory = (categoryKey, subcategoryKey) => {
  const key = canonicalizeCategoryKey(categoryKey, categories);
  return categories[key]?.subcategories?.[subcategoryKey]?.types || [];
};

export const getCategoriesForBusinessType = (businessType) => {
  const typeId = resolveBusinessTypeId(businessType);
  if (!typeId) return categories;

  const config = businessTypes[typeId];
  if (!config) return categories;

  const filteredCategories = {};
  config.allowedCategories.forEach((categoryKey) => {
    if (categories[categoryKey]) {
      filteredCategories[categoryKey] = categories[categoryKey];
    }
  });

  return filteredCategories;
};

export const getCategoryOptionsForBusinessType = (businessType, language = 'fr') => {
  const filteredCategories = getCategoriesForBusinessType(businessType);
  return Object.keys(filteredCategories).map((key) => ({
    value: key,
    label: getCategoryLabel(key, language),
  }));
};

export const getCategoryLabel = (categoryKey, language = 'fr') => {
  const key = canonicalizeCategoryKey(categoryKey, categories);
  return getTranslatedLabel(categories, key, language);
};

export const getSubcategoryLabel = (categoryKey, subcategoryKey, language = 'fr') => {
  const key = canonicalizeCategoryKey(categoryKey, categories);
  const category = categories[key];
  if (category?.subcategories?.[subcategoryKey]) {
    return getTranslatedLabel(category.subcategories, subcategoryKey, language);
  }
  for (const cat of Object.values(categories)) {
    if (cat?.subcategories?.[subcategoryKey]) {
      return getTranslatedLabel(cat.subcategories, subcategoryKey, language);
    }
  }
  return subcategoryKey;
};

function findTypeLabelGlobally(typeKey, language = 'fr') {
  if (!typeKey) return null;
  for (const category of Object.values(categories)) {
    if (!category?.subcategories) continue;
    for (const subcategory of Object.values(category.subcategories)) {
      if (!Array.isArray(subcategory?.types)) continue;
      if (subcategory.types.length > 0 && typeof subcategory.types[0] === 'object') {
        const type = subcategory.types.find((t) => t.key === typeKey);
        if (type) return language === 'ar' ? type.nameAr : type.name;
      }
    }
  }
  return null;
}

export const getTypeLabel = (categoryKey, subcategoryKey, typeKey, language = 'fr') => {
  const key = canonicalizeCategoryKey(categoryKey, categories);
  const category = categories[key];
  if (!category?.subcategories?.[subcategoryKey]) {
    const globalType = findTypeLabelGlobally(typeKey, language);
    if (globalType) return globalType;
    return typeKey;
  }

  const subcategory = category.subcategories[subcategoryKey];

  if (Array.isArray(subcategory.types)) {
    if (subcategory.types.length > 0 && typeof subcategory.types[0] === 'object') {
      const type = subcategory.types.find((t) => t.key === typeKey);
      if (type) {
        return language === 'ar' ? type.nameAr : type.name;
      }
      const globalType = findTypeLabelGlobally(typeKey, language);
      if (globalType) return globalType;
    } else {
      const typeName = subcategory.types.find((t) => t === typeKey);
      if (typeName) return typeName;
    }
  }

  const globalType = findTypeLabelGlobally(typeKey, language);
  if (globalType) return globalType;

  return typeKey;
};

export const resolveCategoryDisplay = (value, language = 'fr') => {
  if (!value) return '';
  const key = canonicalizeCategoryKey(value, categories);
  if (categories[key]) {
    return getTranslatedLabel(categories, key, language);
  }
  return value;
};

export const getBusinessTypeLabel = (businessTypeId, language = 'fr') => {
  return getTranslatedLabel(businessTypes, businessTypeId, language);
};

/**
 * Integrity snapshot for tests — does not throw.
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function assertCatalogIntegrity() {
  const errors = [];
  const treeKeys = new Set(Object.keys(categories));
  const unassigned = new Set(UNASSIGNED_CATEGORY_KEYS);

  for (const [btId, cfg] of Object.entries(businessTypes)) {
    if (!Array.isArray(cfg.allowedCategories) || cfg.allowedCategories.length === 0) {
      errors.push(`${btId}: allowedCategories empty`);
      continue;
    }
    for (const catKey of cfg.allowedCategories) {
      if (!treeKeys.has(catKey)) {
        errors.push(`${btId}: allowed category missing from tree: ${catKey}`);
      }
    }
  }

  for (const key of treeKeys) {
    const assigned = Object.values(businessTypes).some((cfg) =>
      cfg.allowedCategories.includes(key)
    );
    if (!assigned && !unassigned.has(key)) {
      errors.push(`orphan category (not assigned, not UNASSIGNED): ${key}`);
    }
  }

  for (const key of unassigned) {
    if (!treeKeys.has(key)) {
      errors.push(`UNASSIGNED key not in tree: ${key}`);
    }
  }

  return { ok: errors.length === 0, errors };
}
